/**
 * PhotoCapture Component
 *
 * Multi-photo capture component for incident reports.
 * Uses device camera, compresses images, and shows upload progress.
 *
 * @module pages/technician/incidents/PhotoCapture
 */

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FiCamera, FiX, FiImage, FiLoader } from 'react-icons/fi';
import './IncidentReportForm.css';

/**
 * Compress image to reduce file size
 * @param {File} file - Original image file
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<Blob>} Compressed image blob
 */
const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Error al comprimir imagen'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => reject(new Error('Error al cargar imagen'));
    };
    reader.onerror = () => reject(new Error('Error al leer archivo'));
  });
};

const PhotoCapture = ({
  photos,
  onPhotosChange,
  maxPhotos,
  disabled,
  uploading,
  uploadProgress,
}) => {
  const fileInputRef = useRef(null);
  const [processingIndex, setProcessingIndex] = useState(null);

  const handleCaptureClick = () => {
    if (!disabled && photos.length < maxPhotos) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const remainingSlots = maxPhotos - photos.length;
    const filesToProcess = files.slice(0, remainingSlots);

    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        console.warn('Archivo no es una imagen:', file.name);
        continue;
      }

      setProcessingIndex(photos.length + i);

      try {
        // Compress image
        const compressedBlob = await compressImage(file);

        // Create a new File object with the compressed data
        const compressedFile = new File(
          [compressedBlob],
          file.name.replace(/\.[^/.]+$/, '.jpg'),
          { type: 'image/jpeg' }
        );

        // Create preview URL
        const previewUrl = URL.createObjectURL(compressedBlob);

        // Add to photos array
        const newPhoto = {
          file: compressedFile,
          preview: previewUrl,
          name: file.name,
          size: compressedFile.size,
          uploaded: false,
          url: null,
        };

        onPhotosChange([...photos, newPhoto]);
      } catch (error) {
        console.error('Error procesando imagen:', error);
      }
    }

    setProcessingIndex(null);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (index) => {
    if (disabled || uploading) return;

    const photoToRemove = photos[index];

    // Revoke object URL to free memory
    if (photoToRemove.preview && photoToRemove.preview.startsWith('blob:')) {
      URL.revokeObjectURL(photoToRemove.preview);
    }

    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(newPhotos);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canAddMore = photos.length < maxPhotos && !disabled && !uploading;

  return (
    <div className="photo-capture">
      {/* Photo Grid */}
      <div className="photo-grid">
        {photos.map((photo, index) => (
          <div key={index} className="photo-item">
            <div className="photo-preview">
              <img src={photo.preview || photo.url} alt={`Foto ${index + 1}`} />

              {/* Upload progress overlay */}
              {uploading && uploadProgress && uploadProgress[index] !== undefined && (
                <div className="photo-upload-overlay">
                  <div className="upload-progress-ring">
                    <FiLoader className="spinning" size={24} />
                  </div>
                  <span className="upload-percent">
                    {Math.round(uploadProgress[index])}%
                  </span>
                </div>
              )}

              {/* Uploaded indicator */}
              {photo.uploaded && (
                <div className="photo-uploaded-badge">
                  <FiImage size={14} />
                </div>
              )}
            </div>

            {/* Remove button */}
            {!disabled && !uploading && (
              <button
                type="button"
                className="photo-remove-btn"
                onClick={() => handleRemovePhoto(index)}
                aria-label={`Eliminar foto ${index + 1}`}
              >
                <FiX size={16} />
              </button>
            )}

            {/* File info */}
            <div className="photo-info">
              <span className="photo-index">#{index + 1}</span>
              {photo.size && (
                <span className="photo-size">{formatFileSize(photo.size)}</span>
              )}
            </div>
          </div>
        ))}

        {/* Processing placeholder */}
        {processingIndex !== null && (
          <div className="photo-item processing">
            <div className="photo-preview processing-preview">
              <FiLoader className="spinning" size={32} />
              <span>Procesando...</span>
            </div>
          </div>
        )}

        {/* Add photo button */}
        {canAddMore && processingIndex === null && (
          <button
            type="button"
            className="photo-add-btn"
            onClick={handleCaptureClick}
            aria-label="Agregar foto"
          >
            <FiCamera size={28} />
            <span>Agregar</span>
            <span className="photo-count">
              {photos.length}/{maxPhotos}
            </span>
          </button>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={disabled || uploading}
      />

      {/* Helper text */}
      <div className="photo-helper">
        {photos.length === 0 ? (
          <span>Toca para capturar evidencia fotografica</span>
        ) : photos.length >= maxPhotos ? (
          <span className="text-warning">Maximo de fotos alcanzado</span>
        ) : (
          <span>{maxPhotos - photos.length} foto(s) disponible(s)</span>
        )}
      </div>
    </div>
  );
};

PhotoCapture.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.object,
      preview: PropTypes.string,
      name: PropTypes.string,
      size: PropTypes.number,
      uploaded: PropTypes.bool,
      url: PropTypes.string,
    })
  ).isRequired,
  onPhotosChange: PropTypes.func.isRequired,
  maxPhotos: PropTypes.number,
  disabled: PropTypes.bool,
  uploading: PropTypes.bool,
  uploadProgress: PropTypes.object,
};

PhotoCapture.defaultProps = {
  maxPhotos: 5,
  disabled: false,
  uploading: false,
  uploadProgress: null,
};

export default PhotoCapture;
